import axios from 'axios';
import {
  CommonState,
  HomrPageTypes,
  HomrPageTaxosMap,
  TaxonomyResponse,
  HomrTaxonomyResponses,
  HomrTaxonomyResponseKeys,
  StoreIdentifierName,
  ReduxState,
} from 'types';
import { Request } from 'express';
import { envData } from 'environments';
import getConfigByEnvironment from 'config/environmentConfig';

// eslint-disable-next-line import/prefer-default-export
export const handleOverrideFeatureFlags = (envFeatureFlags, overrideFlags) => {
  // Handles the feature flag overrides by merging them into the environment feature flags if there are any
  const featureFlags = { ...envFeatureFlags };

  if (
    !overrideFlags ||
    typeof overrideFlags !== 'object' ||
    Object.keys(overrideFlags).length === 0
  )
    return featureFlags;

  Object.keys(featureFlags).forEach((key) => {
    if (overrideFlags[key]) {
      featureFlags[key] = { ...featureFlags[key], ...overrideFlags[key] };
    }
  });

  return featureFlags;
};

export const getFeatureFlags = (req: Request, envFeatureFlags: CommonState['featureFlags']) => {
  // Get feature flag overrides from the request header and merge them with the environment feature flags
  let featureFlagOverrides = {};
  try {
    const reqHeaderFeatureFlag = req.header('X-DSG-Header-Feature-Flag');
    if (reqHeaderFeatureFlag) {
      featureFlagOverrides = JSON.parse(reqHeaderFeatureFlag);
    }
  } catch (error) {
    console.error('Error parsing X-DSG-Header-Feature-Flag:', error);
  }
  const featureFlagsWithOverrides = handleOverrideFeatureFlags(
    envFeatureFlags,
    featureFlagOverrides,
  );

  return featureFlagsWithOverrides;
};

export const getFilteredHeaders = (headers: { [x: string]: string }, allowedHeaders: string) => {
  // Filters the headers to only include the allowed headers
  const filteredHeaders: Record<string, any> = {};
  Object.keys(headers).forEach((key) => {
    if (key.toUpperCase().startsWith(allowedHeaders.toUpperCase())) {
      filteredHeaders[key] = headers[key];
    }
  });
  return filteredHeaders;
};

export const getFeatureFlagEnv = () => (process.env.LOCAL_DEV ? 'perf' : process.env.profile);

export const getStoreIdentifiers = () => {
  // TypeScript return string[] for Object.keys in all cases due to several
  // edge-cases, but since we're not intending to ever modify envData after
  // the fact, I think it's safe to cast this to ``keyof typeof envData``
  return Object.keys(envData) as (keyof typeof envData)[];
};

export const shouldStoreUseProgressiveHeaderTaxo = (
  storeIdentifier: StoreIdentifierName,
  featureFlags: CommonState['featureFlags'],
) => {
  if (featureFlags) {
    const progressiveTaxoStores = featureFlags.progressiveTaxoDesktop;
    if (storeIdentifier in progressiveTaxoStores) {
      return progressiveTaxoStores[storeIdentifier as keyof typeof progressiveTaxoStores]; // casting here because we confirmed above that the key was in the store
    }
  }
  return false;
};

// excluding modal and ticker data for now because we don't preload
// that data on the server currently and don't store that data alongside
// the rest of our taxos. In the future, this might be something we can add
//
// Blair Wilcox 6/6/2025
export const fetchTaxo = <T extends Exclude<HomrPageTypes, 'modal' | 'ticker'>>(
  // homrUrl: string,
  pageType: T,
  pageName: string,
  chain: StoreIdentifierName,
) => {
  const featureFlagEnv = getFeatureFlagEnv();
  const homrUrl = getConfigByEnvironment(featureFlagEnv).api.homr;

  try {
    return axios
      .get<HomrPageTaxosMap[T], TaxonomyResponse<HomrPageTaxosMap[T]>>(homrUrl, {
        headers: {
          ...(process.env.PLATFORM && process.env.PLATFORM === 'AKS'
            ? { 'X-DSG-Platform': 'v2' }
            : {}),
        },
        params: { pageType, name: pageName, chain },
        timeout: 10000,
      })
      .then((taxoResponse) => {
        // If the response is successful, return the response
        if (taxoResponse.status === 200) {
          return taxoResponse;
        }

        return undefined;
      })
      .catch((error) => {
        console.error(
          `${pageType} NODE taxonomy cache refresh failed for ${chain}. Error Message:`,
          error instanceof Error ? error.message : String(error),
        );
        return undefined;
      });
  } catch (error: unknown) {
    console.error(
      `${pageType} NODE taxonomy cache refresh failed for ${chain}. Error Message:`,
      error instanceof Error ? error.message : String(error),
    );
    return undefined;
  }
};

export const fetchTaxosForBrand = async (
  storeIdentifier: StoreIdentifierName,
  commonState: ReduxState['common'],
  route: 'header' | 'footer' = 'header',
): Promise<Partial<HomrTaxonomyResponses>> => {
  /* NOTE (From: Holden): For now we need to keep progressive taxo turned on server side by default due to the fact 
    our UI code does not currently support empty rendering without breaking. 
    If we try to turn it off and a test enables progressive taxo desktop on the client the data 
    may not exist and break when trying to switch. Enabling by default for dsg for server side only for now. */
  const shouldUseProgressiveTaxo =
    storeIdentifier === 'dsg' && route === 'header'
      ? true
      : shouldStoreUseProgressiveHeaderTaxo(storeIdentifier, commonState?.featureFlags);
  const taxos: Partial<HomrTaxonomyResponses> = {};

  const taxoKeysNeeded = [
    ...(route === 'header' ? ['header'] : []),
    ...(route === 'footer' ? ['footer'] : []),
    ...(route === 'header' && shouldUseProgressiveTaxo
      ? ['progressiveHeader', 'headerFeatured']
      : []),
  ];

  const taxoKeyTranslation: Record<
    string,
    'header' | 'footer' | 'progressive-header' | 'header-featured'
  > = {
    header: 'header',
    footer: 'footer',
    progressiveHeader: 'progressive-header',
    headerFeatured: 'header-featured',
  };
  const taxoPromises = taxoKeysNeeded.map((taxoKey) => {
    return fetchTaxo(taxoKeyTranslation[taxoKey], 'default', storeIdentifier);
  });

  const taxoResolutions = await Promise.allSettled(taxoPromises);

  taxoKeysNeeded.forEach((taxoKey, index) => {
    const taxoResolution = taxoResolutions[index];
    if (taxoResolution.status === 'fulfilled' && taxoResolution.value) {
      // Dynamic assignment - the type system now supports this
      taxos[taxoKey as HomrTaxonomyResponseKeys] = taxoResolution.value;
    }
  });

  return taxos;
};
